import { dateTermVoService } from '../../domain/value-object/DateTermVO'
import DateTimeTermVO from '../../domain/value-object/DateTimeTermVO'
import { WbsItemSearchConditionVO } from '../../domain/value-object/WbsItemSearchConditionVo'
import objects from '../../utils/objects'
import DateTimeVO from '../../domain/value-object/DateTimeVO'
import { ApiResponseValue } from '../model/api'
import { adaptSearchConditionBefore20230125 } from '../compatible-adaptors/wbsItemSearchConditionCompatibleAdaptor'

const wbsItemSearchConditionVoKeys: Record<
  keyof WbsItemSearchConditionVO,
  true
> = {
  searchText: true,
  rootUuid: true,
  code: true,
  types: true,
  ticketTypes: true,
  status: true,
  priority: true,
  displayName: true,
  description: true,
  teamUuid: true,
  accountableUuid: true,
  responsibleUuid: true,
  assigneeUuid: true,
  deliverableUuid: true,
  scheduledStartDate: true,
  scheduledEndDate: true,
  actualStartDate: true,
  actualEndDate: true,
  sprintUuid: true,
  sprintUuids: true,
  tagUuids: true,
  blank: true,
  createdAt: true,
  updatedAt: true,
  createdByUuid: true,
  updatedByUuid: true,
}

const stringFields = new Set([
  'searchText',
  'rootUuid',
  'code',
  'displayName',
  'description',
  'teamUuid',
  'accountableUuid',
  'responsibleUuid',
  'assigneeUuid',
  'deliverableUuid',
  'sprintUuid',
  'createdByUuid',
  'updatedByUuid',
])

const stringArrayFields = new Set([
  'types',
  'ticketTypes',
  'status',
  'priority',
  'sprintUuids',
  'tagUuids',
])

type WbsItemSearchConditionFactory = {
  fromApiResponseToVo: (src: ApiResponseValue) => WbsItemSearchConditionVO
}

export const useWbsItemSearchConditionFactory =
  (): WbsItemSearchConditionFactory => {
    const fromApiResponseToVo = (
      src: ApiResponseValue
    ): WbsItemSearchConditionVO => {
      if (!src) return {}
      const adapted = adaptSearchConditionBefore20230125(src)
      const dest: WbsItemSearchConditionVO = {}
      for (let entry of Object.entries(adapted)) {
        const key = entry[0]
        const value = entry[1]
        if (!value) continue
        // Skip keys not in WbsItemSearchConditionVO
        if (!(key in wbsItemSearchConditionVoKeys)) continue
        // String fields: skip if not string
        if (stringFields.has(key)) {
          if (typeof value !== 'string') continue
          objects.setValue(dest, key, value)
          continue
        }
        // String array fields: skip if not string[]
        if (stringArrayFields.has(key)) {
          if (
            !Array.isArray(value) ||
            value.some((v: unknown) => typeof v !== 'string')
          ) {
            continue
          }
          objects.setValue(dest, key, value)
          continue
        }
        if (
          [
            'scheduledStartDate',
            'scheduledEndDate',
            'actualStartDate',
            'actualEndDate',
          ].includes(key)
        ) {
          if (typeof value !== 'object') continue
          const v = dateTermVoService.construct(value['from'], value['to'])
          objects.setValue(dest, key, v)
          continue
        }
        if (['createdAt', 'updatedAt'].includes(key)) {
          const v = new DateTimeTermVO(
            !!value['from'] ? new DateTimeVO(value['from']) : undefined,
            !!value['to'] ? new DateTimeVO(value['to']) : undefined
          )
          objects.setValue(dest, key, v)
          continue
        }
        objects.setValue(dest, key, value)
      }
      return dest
    }
    return {
      fromApiResponseToVo,
    }
  }
